import React from 'react';
import { lazily } from 'react-lazily';
import { CurrencyAmount } from 'src/components/currency-amount';
import { SubventionOffer } from 'src/components/default-promo-widget/components/ltf-snippet/components/subvention-offer/subvention-offer';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import { INSTALLMENT_PLANS } from 'src/constants/learn-more';
import { useAppContext } from 'src/context/store/app-provider';
import { useGetSubvention } from 'src/hooks/use-get-subvention';
import { useLocale } from 'src/hooks/use-locale';
import { createNormalizeCheckingNumber } from 'src/utils/create-normalize-checking-number';
import { calculatePriceWithFees } from 'src/utils/default-fees-calculator';
import { findExperiment } from 'src/utils/find-experiment';
import { getFormattedAmount } from 'src/utils/get-formatted-amount';
import { getPriceSplitOf } from 'src/utils/get-price-split-of';

import styles from './ltf-snippet.css';

const { SnippetDefault } = lazily(
  () => import('src/components/snippet-default')
);

interface Props {
  onClick: () => void;
}

export const LtfSnippet = ({ onClick }: Props) => {
  const {
    currency,
    isRtl,
    priceRaw,
    offers: { installments: offersInstallments = [] },
    installmentsCount,
    experiments,
  } = useAppContext();
  const t = useLocale();

  const preparedPrice = createNormalizeCheckingNumber(
    'Price argument is not a number',
    0
  )(priceRaw);

  const country = COUNTRY_NAME_BY_CURRENCY_MAP[currency];

  const shariahText =
    country === 'saudi' ? `${t('default.shariahCompliant')}.` : '';

  const subvention = useGetSubvention();

  const longestOfferPaymentsCount = Math.max(
    0,
    ...offersInstallments.map((offer) => offer.paymentsCount)
  );

  const offerWithSubvention = offersInstallments.reduce(
    (maxOffer, offer) =>
      offer.subventionCampaignId &&
      (!maxOffer || offer.paymentsCount > maxOffer.paymentsCount)
        ? offer
        : maxOffer,
    undefined as (typeof offersInstallments)[0] | undefined
  );
  const isLtfKsaSnippetExp =
    findExperiment('BNPL-11864-KSA-LTF-snippet', experiments) === 'test';

  const getText = () => {
    const sortedPlansByAmount = offersInstallments
      .slice()
      .sort((a, b) => a.amount - b.amount);
    const sortedPlansByInstallmentsCount = offersInstallments
      .slice()
      .sort((a, b) => b.paymentsCount - a.paymentsCount);

    switch (true) {
      case !!preparedPrice && !!installmentsCount && subvention.isSubvention: {
        return {
          text: (
            <span>
              {t(
                'split_your_purchase_into_x_interest_free_payments',
                {
                  installmentsCount:
                    subvention.longestSubventionPlan?.paymentsCount,
                },
                {
                  amount: () => (
                    <CurrencyAmount
                      currency={currency}
                      currencyPosition={isRtl ? 'end' : 'start'}
                    >
                      {subvention.longestSubventionPlan?.amount}
                    </CurrencyAmount>
                  ),
                }
              )}{' '}
              {shariahText}
            </span>
          ),
          link: <span>{t('default.learnMore')}</span>,
        };
      }
      case !!preparedPrice && isLtfKsaSnippetExp && country === 'saudi': {
        const longestPlan = sortedPlansByInstallmentsCount[0];
        const price = longestPlan?.amount
          ? getFormattedAmount(longestPlan.amount, currency)
          : getPriceSplitOf(preparedPrice, currency, INSTALLMENT_PLANS.twelve);
        const paymentsCount =
          longestOfferPaymentsCount || INSTALLMENT_PLANS.twelve;
        const amountComponent = {
          amount: () => (
            <CurrencyAmount
              currency={currency}
              currencyPosition={isRtl ? 'end' : 'start'}
              suffix={`/${t('month_short')}`}
            >
              {price}
            </CurrencyAmount>
          ),
        };

        return {
          text: (
            <span>
              {paymentsCount < 11
                ? t(
                    'from_amount_with_up_to_count_monthly_payments_few',
                    { installmentsCount: paymentsCount },
                    amountComponent
                  )
                : t(
                    'from_amount_with_up_to_count_monthly_payments_other',
                    { installmentsCount: paymentsCount },
                    amountComponent
                  )}{' '}
              {`${shariahText}`}
            </span>
          ),
          link: <span>{t('default.learnMore')}</span>,
        };
      }
      case !preparedPrice && isLtfKsaSnippetExp && country === 'saudi': {
        const paymentsCount =
          longestOfferPaymentsCount || INSTALLMENT_PLANS.twelve;

        return {
          text: (
            <span>
              {paymentsCount < 11
                ? t('pay_in_up_to_count_monthly_payments_few', {
                    installmentsCount: paymentsCount,
                  })
                : t('pay_in_up_to_count_monthly_payments_other', {
                    installmentsCount: paymentsCount,
                  })}
              <br />
              {`${shariahText}`}
            </span>
          ),
          link: <span>{t('default.learnMore')}</span>,
          classNameLink: styles.ltfSnippet_inlineText,
          classNameText: styles.ltfSnippet_inlineText,
        };
      }
      case !!preparedPrice && !!installmentsCount: {
        const cheapestInstallmentPlan = sortedPlansByAmount[0];

        const price = cheapestInstallmentPlan?.amount
          ? getFormattedAmount(cheapestInstallmentPlan.amount, currency)
          : getPriceSplitOf(
              country === 'saudi'
                ? preparedPrice
                : calculatePriceWithFees({
                    price: preparedPrice,
                    installmentCount: INSTALLMENT_PLANS.twelve,
                  }),
              currency,
              INSTALLMENT_PLANS.twelve
            );

        return {
          text: (
            <span>
              {t(
                'paid_plans.as_low_as_per_month_ltp',
                {
                  installmentsCount: 4,
                  price,
                },
                {
                  amount: () => (
                    <CurrencyAmount
                      currency={currency}
                      currencyPosition={isRtl ? 'end' : 'start'}
                      suffix={`/${t('month')}`}
                    >
                      {price}
                    </CurrencyAmount>
                  ),
                }
              )}{' '}
              {`${shariahText}`}
            </span>
          ),
          link: <span>{t('default.learnMore')}</span>,
        };
      }
      case (!preparedPrice || !installmentsCount) && country === 'emirates':
        return {
          text: (
            <span>
              {t('split_your_purchase_in_up_to_x_monthly_payments', {
                installmentsCount:
                  longestOfferPaymentsCount || INSTALLMENT_PLANS.twelve,
              })}
            </span>
          ),
          link: <span>{t('default.learnMore')}</span>,
        };
      case (!preparedPrice || !installmentsCount) && country === 'saudi':
        return {
          text: (
            <span>
              {t('split_your_purchase_in_up_to_x_monthly_payments', {
                installmentsCount:
                  longestOfferPaymentsCount || INSTALLMENT_PLANS.twelve,
              })}{' '}
              {t('default.shariahCompliant')}.
            </span>
          ),
          link: <span>{t('default.learnMore')}</span>,
        };
      default:
        return {
          text: <span>{t('tabbySplit.3_how_it_works')}</span>,
          link: <span>{t('default.learnMore')}</span>,
        };
    }
  };

  const additionalContent = offerWithSubvention ? (
    <SubventionOffer offer={offerWithSubvention} />
  ) : null;

  const { text, link, classNameText, classNameLink } = getText();

  return (
    <SnippetDefault
      message={text}
      linkText={link}
      classNameText={classNameText}
      classNameLink={classNameLink}
      onClick={onClick}
      additionalContent={additionalContent}
    />
  );
};
